import Vue from "vue";
import VueRouter from "vue-router";
import { TokenService } from "@/api/storage";
import { initialize } from "@/api";
import Store from "../store";
import { updateMetaTags } from "../utils/metaTagsService";

Vue.use(VueRouter);

const routes = [
  {
    path: "/home/:user/:language?",
    name: "home",
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/Home.vue"),
    meta: {
      displayName: "Home - Exclusive Lounge",
      requiresAuth: false,
      title: "Exclusive Lounge",
    },
  },
  {
    path: "/pagina-nao-encontrada",
    name: "notFound",
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/NotFound.vue"),
    meta: {
      displayName: "404 - Exclusive Lounge",
      requiresAuth: false,
      title: "Exclusive Lounge",
    },
  },
  {
    path: "/pagina-sem-autenticacao",
    name: "notAuth",
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/NotAuth.vue"),
    meta: {
      displayName: "404 - Exclusive Lounge",
      requiresAuth: false,
      title: "Exclusive Lounge",
    },
  },
  {
    path: "/transparencia-dados",
    name: "transparency",
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/Transparency.vue"),
    meta: {
      displayName: "Transparência - Exclusive Lounge",
      requiresAuth: true,
      title: "Exclusive Lounge",
    },
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

router.beforeEach((to, from, next) => {
  // Atualiza as meta tags com base na rota atual
  if (to.meta && to.meta.title) {
    const metaOptions: {
      title: string;
      description: string;
      url: string;
      image?: string;
    } = {
      title: to.meta.title as string,
      description: (to.meta.description as string) || "Social Media for CIO's",
      url: window.location.origin + to.fullPath,
    };

    // Se a rota tiver uma imagem específica, use-a
    if (to.meta.image) {
      metaOptions.image = to.meta.image as string;
    }

    // Atualiza as meta tags
    updateMetaTags(metaOptions);
  }

  // Buscar tokens uma vez no início para usar em todas as condições
  const token = TokenService.getToken();
  const refreshToken = TokenService.getRefreshToken();
  const firstAccess = TokenService.getFirstAccess();

  if (to.matched.length === 0) {
    next({ name: "notFound" });
  } else if (to?.meta?.requiresAuth) {
    // redirect to onboarding if the user didn't completed the onboarding
    if (
      token &&
      firstAccess === "true" &&
      to.name !== "onboardingPage" &&
      to.name !== "profileStep" &&
      from.name !== "loginPage" &&
      from.name !== "home"
    ) {
      next({ name: "loginPage" });
    }
    // redirect to login if the user to try acess any other route without login
    // Verifica se não tem token E não tem refresh token (usuário realmente não está logado)
    else if (
      !token &&
      !refreshToken &&
      to.name !== "onboardingPage" &&
      to.name !== "profileStep" &&
      from.name !== "loginPage" &&
      to.name !== "timelineHome" &&
      from.name !== "home"
    ) {
      Store.dispatch("updateRouteAfterLogin", to);
      next({ name: "loginPage" });
    }
    // Se tem refresh token mas não tem token, permite navegar
    // O interceptor vai tentar renovar o token automaticamente
    else {
      initialize();
      const isReturn =
        (to.name === "settingsHome" && from.name === "settingsMetricsPost") ||
        ([
          "searchHome",
          "timelineHome",
          "readingListSuccessCase",
          "readingListWhitePaperList",
          "readingListItems",
          "profileHome",
          "profileCLevel",
          "profileMarketing",
        ].includes(`${to.name}`) &&
          [
            "profileCLevel",
            "profileMarketing",
            "timelinePost",
            "searchTagId",
            "roundTableItems",
            "newPostRoundTable",
            "communityHome",
          ].includes(`${from.name}`) &&
          (Store.state.lastItemsPost.length > 0 ||
            Store.state.lastItemsUser.length > 0));
      Store.dispatch("updateReturn", isReturn);
      if (from.name === "searchHome") Store.dispatch("updateFilter", "");
      if (from.name !== null && !isReturn && to.name !== "timelinePost") {
        TokenService.saveTab("0");
      }
      next();
    }
  } else if (token && (firstAccess === "false" || !firstAccess)) {
    initialize();
    next({ name: "timelineHome" });
  } else {
    next();
  }
});

// Função para atualizar meta tags com base na rota atual
function updateMetaTagsFromRoute(route: any) {
  if (route.meta && route.meta.title) {
    updateMetaTags({
      title: route.meta.title as string,
      description:
        (route.meta.description as string) || "Social Media for CIO's",
      url: window.location.origin + route.fullPath,
      image: route.meta.image as string | undefined,
    });
  } else {
    // Caso não tenha meta tags específicas, use as padrões mas com a URL atual
    updateMetaTags({
      url: window.location.href,
    });
  }
}

// Atualiza as meta tags quando o router estiver pronto
router.onReady(() => {
  // Atualiza as meta tags com base na rota inicial
  updateMetaTagsFromRoute(router.currentRoute);
});

// Atualiza as meta tags sempre que a rota mudar
router.afterEach((to) => {
  updateMetaTagsFromRoute(to);
});

export default router;
